import React, { useEffect, useMemo, useState } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { AlertCircle, ChevronDownIcon, HelpCircle, RotateCw } from "lucide-react";
import { TokenBucket, TokenUsageCoverage } from "../../../../types";
import { getTokenUsageCoverage } from "../../../../service/apiService";

/**
 * Attributed (recorded per user in-app) vs account-wide (CloudWatch) Bedrock
 * tokens for a month.
 *
 * The two are shown side by side and never summed: they measure overlapping
 * things over slightly different windows. The gap between them is the point of
 * this panel -- it is the usage we cannot tie to a person (BrainBank ingestion,
 * whatever Nova Act drives inside AgentCore, any call path not yet instrumented).
 */

const formatTokens = (n: number) => (n || 0).toLocaleString();

/** 1.2M / 4.5K / 812 - keeps the big stat tiles readable at a glance. */
const formatCompact = (n: number) => {
  const value = n || 0;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}K`;
  return String(value);
};

/** Turn "image-studio" into "Image Studio"; keep known acronyms uppercase. */
const FEATURE_LABELS: Record<string, string> = {
  chat: "Chat",
  voice: "Voice",
  "va-builder": "VA Builder",
  brainbank: "BrainBank",
  "image-studio": "Image Studio",
  "file-analysis": "File Analysis",
  "prompt-cards": "Prompt Cards",
  "document-export": "Document Export",
  meetings: "Meetings",
  workflows: "Workflows",
  email: "Email",
  api: "API",
};

const labelForFeature = (key: string) =>
  FEATURE_LABELS[key] ||
  key.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

/**
 * Reduce a model id to a canonical name.
 *
 * The two sides of this table name the same model differently: CloudWatch
 * reports "claude-sonnet-4-6" or "global.anthropic.claude-sonnet-4-6", while we
 * record whatever the app passed, e.g. "us.anthropic.claude-sonnet-4-6-v1:0".
 * Grouping both sides by this key is what lets them line up at all - without it
 * every account row looks like usage from outside the app.
 */
const canonicalModel = (modelId: string) => {
  if (!modelId || modelId === "unknown") return "unknown";
  return modelId
    .toLowerCase()
    .replace(/^(us|eu|apac|global|us-gov)\./, "")
    .replace(
      /^(anthropic|amazon|meta|mistral|cohere|deepseek|stability|openai|moonshotai|ai21|writer|qwen)\./,
      ""
    )
    .replace(/-v\d+(:\d+)?$/, "")
    .replace(/:\d+$/, "")
    .replace(/-\d{8}$/, "");
};

/** Display form of a canonical model key. */
const shortModelName = (modelId: string) => {
  const key = canonicalModel(modelId);
  return key === "unknown" ? "Unlabelled" : key;
};

const total = (b?: TokenBucket) => (b ? (b.input_tokens || 0) + (b.output_tokens || 0) : 0);

interface Props {
  month: string;
  /** Bumping this refetches - lets the parent resync when its own month changes. */
  refreshKey?: number;
  /**
   * Hands the fetched coverage to the parent. The account-wide totals are also
   * the headline figures on the summary tiles above this panel; surfacing them
   * from the one fetch here keeps the page to a single call to /coverage, which
   * scans the whole usage table for the month.
   */
  onLoaded?: (data: TokenUsageCoverage | null) => void;
}

const TokenUsageCoveragePanel: React.FC<Props> = ({ month, refreshKey = 0, onLoaded }) => {
  const [data, setData] = useState<TokenUsageCoverage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getTokenUsageCoverage(month);
        if (!cancelled) {
          setData(result);
          onLoaded?.(result ?? null);
        }
      } catch (e) {
        console.error("Error fetching token usage coverage:", e);
        if (!cancelled) {
          setError("Couldn't load coverage data. Try again in a moment.");
          onLoaded?.(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [month, refreshKey]);

  const features = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.attributed.by_feature || {})
      .map(([key, bucket]) => ({ key, label: labelForFeature(key), tokens: total(bucket) }))
      .filter((f) => f.tokens > 0)
      .sort((a, b) => b.tokens - a.tokens);
  }, [data]);

  // Tokens we recorded but couldn't label with a model. Rows written before the
  // model-id capture fix land here; while any exist we can't honestly claim a
  // model saw no traffic from the app, so the "outside the app" flag is withheld.
  const unlabelledAttributed = useMemo(
    () =>
      Object.entries(data?.attributed.by_model || {})
        .filter(([key]) => canonicalModel(key) === "unknown")
        .reduce((sum, [, bucket]) => sum + total(bucket), 0),
    [data]
  );

  const models = useMemo(() => {
    if (!data) return [];
    // Group both sides by canonical name so the same model reported under
    // different id formats lands on one row instead of two half-empty ones.
    const rows = new Map<
      string,
      {
        key: string;
        label: string;
        attributed: number;
        account: number;
        ids: string[];
        features: string[];
        role: string;
      }
    >();

    const put = (rawId: string, side: "attributed" | "account", tokens: number) => {
      const key = canonicalModel(rawId);
      if (key === "unknown") return;
      const row =
        rows.get(key) ||
        {
          key,
          label: shortModelName(rawId),
          attributed: 0,
          account: 0,
          ids: [],
          features: [] as string[],
          role: "",
        };
      row[side] += tokens;
      if (!row.ids.includes(rawId)) row.ids.push(rawId);
      rows.set(key, row);
    };

    Object.entries(data.attributed.by_model || {}).forEach(([id, b]) =>
      put(id, "attributed", total(b))
    );
    Object.entries(data.account.by_model || {}).forEach(([id, b]) =>
      put(id, "account", total(b))
    );

    // What each model is used for. Observed usage first - that is evidence, and it
    // stays correct when a user picks a model the config knows nothing about.
    Object.entries(data.attributed.by_model_feature || {}).forEach(([id, feats]) => {
      const row = rows.get(canonicalModel(id));
      if (!row) return;
      Object.entries(feats)
        .filter(([, tokens]) => tokens > 0)
        .sort((a, b) => b[1] - a[1])
        .forEach(([feature]) => {
          const label = labelForFeature(feature);
          if (!row.features.includes(label)) row.features.push(label);
        });
    });

    // Fall back to what the app is configured to use the model for, so a model
    // with no recorded usage yet still says something useful instead of nothing.
    Object.entries(data.model_roles || {}).forEach(([id, role]) => {
      const row = rows.get(canonicalModel(id));
      if (row && !row.role) row.role = role;
    });

    return Array.from(rows.values())
      .filter((m) => m.attributed > 0 || m.account > 0)
      .sort((a, b) => Math.max(b.account, b.attributed) - Math.max(a.account, a.attributed));
  }, [data]);

  if (isLoading) {
    return (
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-center space-x-2 text-gray-500">
          <RotateCw className="animate-spin h-4 w-4" />
          <span className="text-sm">Loading Bedrock coverage…</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">{error || "No coverage data available."}</p>
        </div>
      </div>
    );
  }

  const { attributed, account, coverage_percent, unattributed_tokens } = data;
  const accountAvailable = account.available;
  const coveragePct = coverage_percent ?? 0;
  const attributedPct = accountAvailable && account.total_tokens > 0
    ? Math.min(100, (attributed.total_tokens / account.total_tokens) * 100)
    : 100;

  // Coverage reads as a health signal, but it is a drift indicator rather than a
  // precise ratio, so the thresholds are deliberately forgiving.
  const coverageTone = !accountAvailable
    ? "text-gray-500"
    : coveragePct >= 85
    ? "text-emerald-700"
    : coveragePct >= 60
    ? "text-amber-700"
    : "text-rose-700";

  const maxFeature = features.length ? features[0].tokens : 0;

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-medium text-gray-900">Bedrock Token Coverage</h3>
          <span
            className="group relative inline-flex"
            tabIndex={0}
            aria-label="What this shows"
          >
            <HelpCircle className="h-4 w-4 text-gray-400" />
            <span className="pointer-events-none absolute left-1/2 top-6 z-30 w-72 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal leading-relaxed text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus:opacity-100">
              Attributed tokens are the ones we record per user as requests run.
              Account tokens come from CloudWatch and cover every Bedrock model
              call in the AWS account, including BrainBank ingestion and Nova Act.
              The two are compared, never added.
            </span>
          </span>
        </div>
        {accountAvailable && (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              account.is_final
                ? "bg-gray-100 text-gray-600"
                : "bg-violet-100 text-violet-800"
            }`}
          >
            {account.is_final ? "Final" : "Provisional"}
          </span>
        )}
      </div>

      <div className="px-6 py-5">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 items-stretch">
          <div className="flex h-full min-w-0 flex-col justify-between rounded-lg bg-violet-50 p-4">
            <p className="text-sm font-medium text-violet-600">Attributed to users</p>
            <p
              className="mt-2 whitespace-nowrap text-lg font-bold tabular-nums text-violet-700 xl:text-xl"
              title={`${formatTokens(attributed.total_tokens)} tokens`}
            >
              {formatCompact(attributed.total_tokens)}
            </p>
          </div>
          <div className="flex h-full min-w-0 flex-col justify-between rounded-lg bg-gray-50 p-4">
            <p className="text-sm font-medium text-gray-600">Account total</p>
            <p
              className="mt-2 whitespace-nowrap text-lg font-bold tabular-nums text-gray-800 xl:text-xl"
              title={accountAvailable ? `${formatTokens(account.total_tokens)} tokens` : undefined}
            >
              {accountAvailable ? formatCompact(account.total_tokens) : "—"}
            </p>
          </div>
          <div className="flex h-full min-w-0 flex-col justify-between rounded-lg bg-gray-50 p-4">
            <p className="text-sm font-medium text-gray-600">Unattributed</p>
            <p
              className="mt-2 whitespace-nowrap text-lg font-bold tabular-nums text-gray-800 xl:text-xl"
              title={
                unattributed_tokens != null
                  ? `${formatTokens(unattributed_tokens)} tokens`
                  : undefined
              }
            >
              {unattributed_tokens != null ? formatCompact(unattributed_tokens) : "—"}
            </p>
          </div>
          <div className="flex h-full min-w-0 flex-col justify-between rounded-lg bg-gray-50 p-4">
            <p className="text-sm font-medium text-gray-600">Coverage</p>
            <p className={`mt-2 whitespace-nowrap text-lg font-bold tabular-nums xl:text-xl ${coverageTone}`}>
              {coverage_percent != null ? `${coverage_percent}%` : "—"}
            </p>
          </div>
        </div>

        {/* Coverage bar */}
        {accountAvailable ? (
          <div className="mt-5">
            <div
              className="flex h-3 w-full overflow-hidden rounded-full bg-gray-200"
              role="img"
              aria-label={`${coverage_percent}% of account Bedrock tokens attributed to a user`}
            >
              <div
                className="bg-violet-600 transition-all duration-500"
                style={{ width: `${attributedPct}%` }}
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-violet-600" />
                Attributed to a user
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-gray-300" />
                Not traceable to a user
              </span>
            </div>
          </div>
        ) : (
          <div className="mt-5 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs leading-relaxed text-gray-600">
              Account-wide totals aren't in yet for this month. They arrive from
              CloudWatch once the daily sync runs, and cover Bedrock usage this app
              can't see on its own — BrainBank ingestion and Nova Act's own model
              calls. The per-user numbers below are complete for everything the app
              runs directly.
            </p>
          </div>
        )}

        {/* Where the attributed tokens went */}
        {features.length > 0 && (
          <div className="mt-6">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Attributed usage by feature
            </h4>
            <div className="space-y-2">
              {features.map((feature) => (
                <div key={feature.key} className="flex items-center gap-3">
                  <span className="w-32 flex-shrink-0 truncate text-sm text-gray-700" title={feature.label}>
                    {feature.label}
                  </span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-violet-500"
                      style={{ width: `${maxFeature ? (feature.tokens / maxFeature) * 100 : 0}%` }}
                    />
                  </div>
                  <span
                    className="w-20 flex-shrink-0 text-right text-sm tabular-nums text-gray-600"
                    title={`${formatTokens(feature.tokens)} tokens`}
                  >
                    {formatCompact(feature.tokens)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-model comparison - where the gap actually is */}
        {models.length > 0 && (
          <Disclosure as="div" className="mt-6">
            <DisclosureButton className="group mb-3 flex w-full items-center justify-between rounded-md py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500">
              <span className="flex items-center gap-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 group-hover:text-gray-700">
                  By model
                </h4>
                {/* Count stays visible while collapsed so the section is obviously
                    worth opening, rather than an unlabelled chevron. */}
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                  {models.length}
                </span>
              </span>
              <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400 transition-transform group-hover:text-gray-600 group-data-[open]:rotate-180" />
            </DisclosureButton>
            <DisclosurePanel>
            {unlabelledAttributed > 0 && (
              <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-xs leading-relaxed text-amber-800">
                  <strong>{formatTokens(unlabelledAttributed)} tokens</strong> were
                  recorded without a model name and aren't in this table. They came
                  from calls made before model labelling was fixed, and they are
                  still counted in the totals above. Because they could belong to
                  any model here, the "outside the app" flag is held back until
                  they age out — expect it to return next month.
                </p>
              </div>
            )}
            <p className="mb-2 text-xs text-gray-500">
              <span className="font-medium text-violet-700">Purple chips</span> are
              features we actually recorded using the model.{" "}
              <span className="italic">Italic text</span> means nothing has been
              recorded against it yet, so this is what the app is configured to use
              it for.
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Model
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      What uses it
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                      Attributed
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                      Account
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                      Gap
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {models.map((model) => {
                    const gap = accountAvailable ? Math.max(0, model.account - model.attributed) : null;
                    // A model with account usage but nothing attributed is the
                    // signal worth surfacing: it's work happening outside the app.
                    const untracked =
                      accountAvailable &&
                      model.attributed === 0 &&
                      model.account > 0 &&
                      unlabelledAttributed === 0;
                    return (
                      <tr key={model.key} className={untracked ? "bg-amber-50/60" : undefined}>
                        <td className="px-4 py-2 text-sm text-gray-900" title={model.ids.join(", ")}>
                          <span className="flex items-center gap-2">
                            {model.label}
                            {untracked && (
                              <span
                                className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                                title="Used in the account but not by any request this app handled"
                              >
                                outside the app
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {model.features.length > 0 ? (
                            <span className="flex flex-wrap gap-1">
                              {model.features.map((feature) => (
                                <span
                                  key={feature}
                                  className="inline-flex items-center rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800"
                                  title="Recorded usage from this feature"
                                >
                                  {feature}
                                </span>
                              ))}
                            </span>
                          ) : model.role ? (
                            <span
                              className="text-xs italic text-gray-500"
                              title="No usage recorded against this model yet — this is what the app is configured to use it for"
                            >
                              {model.role}
                            </span>
                          ) : (
                            <span
                              className="text-xs text-gray-400"
                              title="Not pinned by config and not in the LLM list — this model is being used somewhere outside this app"
                            >
                              Not used by this app
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right text-sm tabular-nums text-gray-700">
                          {model.attributed ? formatTokens(model.attributed) : "—"}
                        </td>
                        <td className="px-4 py-2 text-right text-sm tabular-nums text-gray-700">
                          {accountAvailable && model.account ? formatTokens(model.account) : "—"}
                        </td>
                        <td className="px-4 py-2 text-right text-sm tabular-nums text-gray-500">
                          {gap ? formatTokens(gap) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </DisclosurePanel>
          </Disclosure>
        )}

        <p className="mt-4 text-xs leading-relaxed text-gray-400">
          Coverage is a drift signal, not an exact reconciliation — the two sources
          close their windows at different times and meter retries and cached reads
          differently. Image Studio and AgentCore browser sessions bill per image
          and per session-hour rather than per token, so they appear in the Bedrock
          cost reports instead of here.
        </p>
      </div>
    </div>
  );
};

export default TokenUsageCoveragePanel;
